<template>
	<div class="amelipro-table">
		<div
			v-if="!noTableInfos"
			class="d-flex justify-space-between align-end mb-6 amelipro-table__infos"
		>
			<p class="d-inline-block mb-0 total-counter font-weight-semibold">
				<span class="d-sr-only">Nombre de ligne dans le tableau ci-après :</span>
				{{ dataList.length }} {{ counterLabel }}
			</p>

			<div class="d-inline-flex align-center amelipro-table__selects">
				<AmeliproSelect
					v-if="sortSelectItems.length > 0"
					:id="`${id}-sort-select`"
					ref="ameliproSortSelect"
					class="ml-2 amelipro-table__sort-select"
					global-max-width="170px"
					hide-error-message
					:items="sortSelectItems"
					label="Trier par :"
					placeholder="Trier par"
					style="padding-top: 0 !important;"
					@change="emitSortSelectChange"
				/>

				<AmeliproSelect
					v-if="breakpointHelper.isDesktop()"
					:id="`${id}-pagination-select`"
					ref="ameliproPaginationSelect"
					class="ml-2 amelipro-table__pagination-select"
					global-max-width="170px"
					hide-error-message
					:items="paginationSelectItems"
					label="Nb lignes/page :"
					placeholder="Nb lignes/page"
					style="padding-top: 0 !important;"
					@change="emitPaginationSelectChange()"
				/>
			</div>
		</div>
		<div
			v-if="breakpointHelper.isDesktop()"
			class="amelipro-table__wrapper--desktop"
			style="overflow-x: auto;"
			:style="tableWidthStyles"
		>
			<table
				:aria-label="title"
				class="amelipro-table__table--desktop"
				:class="{
					'vertical-border': verticalBorder
				}"
			>
				<thead>
					<tr>
						<th
							v-for="(header, index) in headers"
							:key="index"
							class="ap-grey lighten-4 font-weight-semibold"
							:class="header.headerClasses"
							role="columnheader"
							scope="col"
							:style="`min-width: ${header.minWidth};
							width: ${header.width};
							max-width: ${header.maxWidth};
							text-align: ${header.align};`"
						>
							<p
								:aria-describedby="header.descriptionId"
								class="mb-0"
							>
								{{ header.title }}
							</p>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr
						v-for="(row, rowIndex) in currentDataList"
						:key="rowIndex"
					>
						<td
							v-for="(cell, cellIndex) in datas(row)"
							:key="cellIndex"
							:class="cell.value !== undefined ? cell.columnClasses : undefined"
							:style="cell !== undefined ? `text-align: ${cell.columnAlign};` : undefined"
						>
							<p
								v-if="cell.value !== undefined"
								class="mb-0"
							>
								{{ cell.value }}
							</p>

							<slot
								v-else
								:name="`item-${headers[cellIndex].name}`"
								v-bind="row"
							/>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div
			v-if="!breakpointHelper.isDesktop()"
			class="amelipro-table__wrapper--mobile"
		>
			<div
				v-for="(table, tableIndex) in currentDataList"
				:key="tableIndex"
				class="mb-3"
			>
				<table
					:aria-label="`${title} - partie ${tableIndex + 1}`"
					class="amelipro-table__table--mobile"
				>
					<tr
						v-for="(headeritem, headerindex) in headers"
						:key="headerindex"
					>
						<th
							class="ap-grey lighten-4 font-weight-semibold text-left"
							:class="headeritem.headerClasses"
							role="rowheader"
							scope="row"
							style="width: 50%; max-width: 50%; min-width: 50%;"
						>
							<p class="mb-0">
								{{ headeritem.title }}
							</p>
						</th>
						<td
							class="text-left"
							:class="datas(currentDataList[tableIndex])[headerindex].columnClasses !== undefined ? datas(currentDataList[tableIndex])[headerindex].columnClasses : undefined"
							style="width: 50%; max-width: 50%; min-width: 50%;"
						>
							<p
								v-if="datas(currentDataList[tableIndex])[headerindex].value !== undefined"
								class="mb-0"
							>
								{{ datas(currentDataList[tableIndex])[headerindex].value }}
							</p>
							<slot
								v-else
								:name="`item-${headeritem.name}`"
								v-bind="currentDataList[tableIndex]"
							/>
						</td>
					</tr>
				</table>
			</div>
		</div>

		<AmeliproPagination
			v-if="paginationTable !== undefined && paginationTable.length > 0"
			:active-page-default="1"
			class="mt-2"
			:items="paginationTable"
			@click="emitClickEvent"
		/>
	</div>
</template>

<script setup lang="ts">
	/* eslint-disable arrow-body-style */
	import { AmeliproPaginationSelect, AmeliproSortSelect, AmeliproTableCell, AmeliproTableHeader } from './types';
	import { IDataListItem, IndexedObject } from '../../types';
	import { PropType, computed, onMounted, onUpdated, ref, watch } from 'vue';
	import AmeliproPagination from '../AmeliproPagination/AmeliproPagination.vue';
	import AmeliproSelect from '../AmeliproSelect/AmeliproSelect.vue';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { SelectItem } from '../AmeliproSelect/types';
	import { usePagination } from '../../composables/usePagination';

	const props = defineProps({
		counterLabel: {
			type: String,
			default: 'item(s)',
		},
		dataList: {
			type: Array as PropType<IDataListItem[]>,
			default: () => [],
		},
		headers: {
			type: Array as PropType<AmeliproTableHeader[]>,
			default: () => [],
		},
		hiddenLabels: {
			type: Boolean,
			default: false,
		},
		id: {
			type: String,
			required: true,
		},
		itemsToDisplayDesktop: {
			type: Number,
			default: 10,
		},
		itemsToDisplayMobile: {
			type: Number,
			default: 10,
		},
		noTableInfos: {
			type: Boolean,
			default: false,
		},
		sortSelectItems: {
			type: Array as PropType<SelectItem[]>,
			default: () => [],
		},
		tableMaxWidth: {
			type: String,
			default: '100%',
		},
		tableMinWidth: {
			type: String,
			default: undefined,
		},
		tableWidth: {
			type: String,
			default: '100%',
		},
		title: {
			type: String,
			required: true,
		},
		verticalBorder: {
			type: Boolean,
			default: false,
		},
	});

	const ameliproPaginationSelect = ref<AmeliproPaginationSelect>();
	const ameliproSortSelect = ref<AmeliproSortSelect>();
	const breakpointHelper = new BreakpointHelper();

	const {
		currentPage,
		currentPageItems,
		itemToDisplay,
		paginationSelectItems,
		paginationTable,
		setDefaultItemsPerPage,
		updatePagination,
	} = usePagination(props.dataList, props.itemsToDisplayDesktop, props.itemsToDisplayMobile);

	watch(() => props.dataList, () => {
		updatePagination(props.dataList, itemToDisplay.value);
	});

	onMounted(() => {
		hideSelectLabel();
		setDefaultItemsPerPage();
	});

	onUpdated(() => {
		hideSelectLabel();
	});

	const hideSelectLabel = (): void => {
		if (props.hiddenLabels) {
			document.getElementById(`${props.id}-sort-select-label`)?.setAttribute('class', 'd-sr-only');
			document.getElementById(`${props.id}-pagination-select-label`)?.setAttribute('class', 'd-sr-only');
		}
	};

	const datas = (element: IDataListItem): AmeliproTableCell[] => props.headers.map((headerElement) => ({
		columnAlign: headerElement.align,
		columnClasses: headerElement.cellsClasses,
		value: element[headerElement.name],
	}));

	const currentDataList = computed<IDataListItem[]>(() => currentPageItems(props.dataList));

	const tableWidthStyles = computed<IndexedObject>(() => {
		return {
			maxWidth: props.tableMaxWidth,
			minWidth: props.tableMinWidth,
			width: props.tableWidth,
		};
	});

	const emit = defineEmits(['click', 'change:sort-select', 'change:pagination-select']);
	const emitClickEvent = (newCurrentPage: number): void => {
		emit('click');
		if (newCurrentPage !== null && newCurrentPage !== undefined) {
			currentPage.value = newCurrentPage;
		}
	};

	const emitSortSelectChange = (): void => {
		emit('change:sort-select', ameliproSortSelect.value?.inputValue);
	};

	const emitPaginationSelectChange = (): void => {
		emit('change:pagination-select');
		if (ameliproPaginationSelect.value && (ameliproPaginationSelect.value.inputValue !== null || ameliproPaginationSelect.value.inputValue !== undefined)) {
			updatePagination(props.dataList, ameliproPaginationSelect.value.inputValue);
		}

		if ((currentPage.value !== null && currentPage.value !== undefined) && currentPage.value > paginationTable.value.length) {
			currentPage.value = paginationTable.value.length;
		}

		if (currentPage.value === 0) {
			currentPage.value = 1;
		}
	};
</script>

<style lang="scss" scoped>
.amelipro-table__wrapper--desktop,
.amelipro-table__wrapper--mobile > div {
	border: 1px solid $ap-grey;
	border-radius: 8px;
}

table {
	width: 100%;
	min-width: 100%;
	border-collapse: collapse;
	vertical-align: center;
}

th,
td {
	padding: 1rem;
	border-bottom: 1px solid $ap-grey;

	.amelipro-table__wrapper--mobile table tr:last-child & {
		border-bottom: 0;
	}
}

.amelipro-table__wrapper--mobile {
	& td {
		border-left: 1px solid $ap-grey;
	}

	& table tr:first-child th {
		border-top-left-radius: 8px;
	}

	& table tr:last-child th {
		border-bottom-left-radius: 8px;
	}
}

.vertical-border {
	td, th {
		border-left: 1px solid $ap-grey;
		border-right: 1px solid $ap-grey;
	}

	& tr td:first-child,
	& tr th:first-child {
		border-left: 0;
	}

	& tr td:last-child,
	& tr th:last-child {
		border-right: 0;
	}
}

tr:last-child td {
	border-bottom: 0;
}

.total-counter {
	background-color: $ap-grey-lighten2;
	border: 1px solid $ap-grey-darken1;
	border-radius: $btn-radius;
	padding: 0.75rem 1rem;
}
</style>
